import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "../App";

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

// Convert VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  if (!base64String) {
    console.error('[Push] No VAPID public key provided');
    return null;
  }
  
  try {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  } catch (e) {
    console.error('[Push] Error converting VAPID key:', e);
    return null;
  }
}

// Check support once at module level
const checkPushSupport = () => {
  if (typeof window === 'undefined') return false;
  
  const hasServiceWorker = 'serviceWorker' in navigator;
  const hasPushManager = 'PushManager' in window;
  const hasNotification = 'Notification' in window;
  
  console.log('[Push] Support check:', { hasServiceWorker, hasPushManager, hasNotification, vapidKey: !!VAPID_PUBLIC_KEY });
  
  return hasServiceWorker && hasPushManager && hasNotification && !!VAPID_PUBLIC_KEY;
};

export function usePushNotifications(token) {
  const isSupported = useMemo(() => checkPushSupport(), []);
  const [permission, setPermission] = useState(() => 
    isSupported && typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if already subscribed on mount
  useEffect(() => {
    if (!isSupported) {
      console.log('[Push] Not supported, skipping subscription check');
      return;
    }
    
    let mounted = true;
    
    const checkSubscription = async () => {
      try {
        // Wait for service worker to be ready
        console.log('[Push] Waiting for service worker...');
        const registration = await navigator.serviceWorker.ready;
        console.log('[Push] Service worker ready:', registration.scope);
        
        const subscription = await registration.pushManager.getSubscription();
        console.log('[Push] Current subscription:', !!subscription);
        
        if (mounted) {
          setIsSubscribed(!!subscription);
        }
      } catch (error) {
        console.error('[Push] Error checking subscription:', error);
        if (mounted) {
          setError(error.message);
        }
      }
    };
    
    // Small delay to ensure SW is registered
    const timer = setTimeout(checkSubscription, 1000);
    
    return () => { 
      mounted = false; 
      clearTimeout(timer);
    };
  }, [isSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      console.log('[Push] Not supported');
      setError('Push notifications not supported on this device');
      return false;
    }
    
    if (!token) {
      console.log('[Push] No auth token');
      setError('Please log in first');
      return false;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Request permission
      console.log('[Push] Requesting permission...');
      const permissionResult = await Notification.requestPermission();
      console.log('[Push] Permission result:', permissionResult);
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        setError('Permission denied. Please enable notifications in your browser settings.');
        setLoading(false);
        return false;
      }

      // Get service worker registration
      console.log('[Push] Getting service worker registration...');
      const registration = await navigator.serviceWorker.ready;
      console.log('[Push] Service worker ready:', registration.scope);

      // Get VAPID key
      const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
      if (!applicationServerKey) {
        setError('Invalid VAPID key configuration');
        setLoading(false);
        return false;
      }
      
      console.log('[Push] VAPID key converted, subscribing...');

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });
      
      console.log('[Push] Subscription created:', subscription.endpoint.substring(0, 50) + '...');

      // Send subscription to server
      const subscriptionJson = subscription.toJSON();
      console.log('[Push] Sending subscription to server...');
      
      const response = await axios.post(`${API}/push/subscribe`, {
        subscription: {
          endpoint: subscriptionJson.endpoint,
          keys: subscriptionJson.keys
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[Push] Server response:', response.data);

      setIsSubscribed(true);
      console.log('[Push] Subscription successful!');
      setLoading(false);
      return true;
    } catch (error) {
      console.error('[Push] Error subscribing:', error);
      setError(error.message || 'Error subscribing to notifications');
      setLoading(false);
      return false;
    }
  }, [isSupported, token]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!isSupported || !token) return false;

    setLoading(true);
    setError(null);
    
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Unsubscribe from browser
        await subscription.unsubscribe();

        // Remove from server
        await axios.delete(`${API}/push/unsubscribe`, {
          params: { endpoint: subscription.endpoint },
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setIsSubscribed(false);
      console.log('[Push] Unsubscribed');
      setLoading(false);
      return true;
    } catch (error) {
      console.error('[Push] Error unsubscribing:', error);
      setError(error.message);
      setLoading(false);
      return false;
    }
  }, [isSupported, token]);

  return {
    isSupported,
    permission,
    isSubscribed,
    loading,
    error,
    subscribe,
    unsubscribe
  };
}

export default usePushNotifications;
